import React from 'react';
import {
  LineChart,
  ListChecks,
  Navigation,
  Library,
  ShieldAlert,
  ArrowRight
} from 'lucide-react';

const WelcomeScreen = () => {
  const capabilities = [
    {
      icon: <LineChart size={18} />,
      title: "Win Probability",
      desc: "Data-driven outcome predictions based on historical legal patterns."
    },
    {
      icon: <ListChecks size={18} />,
      title: "Strategic Steps",
      desc: "Actionable milestones and tactical changes recommended for your case."
    },
    {
      icon: <Navigation size={18} />,
      title: "Path Analysis",
      desc: "Multiple strategic approaches with detailed reasoning and risk levels."
    },
    {
      icon: <Library size={18} />,
      title: "Legal Precedent",
      desc: "Automated research into relevant case law and judicial precedents."
    }
  ];

  return (
    <div className="welcome-container">
      <div className="welcome-header">
        <div className="logo-badge">CasePredictAI</div>
        <h1 style={{ color: "#002785" }}>Intelligent Legal Strategy Assistant</h1>
        <p>Transforming complex case data into actionable strategic insights and outcome predictions with high-precision AI analysis.</p>
      </div>

      <div className="caps-grid">
        {capabilities.map((cap, i) => (
          <div key={i} className="cap-card">
            <div className="cap-icon">{cap.icon}</div>
            <div className="cap-content">
              <h3>{cap.title}</h3>
              <p>{cap.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="guide-section">
        <div className="guide-header">
          <ShieldAlert size={16} className="text-blue" />
          <span>Quick Start Guide</span>
        </div>
        <div className="guide-steps">
          <div className="g-step">
            <div className="g-num">1</div>
            <div className="g-text">
              <strong>Upload Documents</strong>
              <p>Share case files or provide a summary of the current legal scenario.</p>
            </div>
          </div>
          <div className="g-step">
            <div className="g-num">2</div>
            <div className="g-text">
              <strong>Run Prediction</strong>
              <p>Get a comprehensive analysis including win probability and risks.</p>
            </div>
          </div>
          <div className="g-step">
            <div className="g-num">3</div>
            <div className="g-text">
              <strong>Consult AI</strong>
              <p>Ask specific questions about tactics, precedents, or defense strategies.</p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .welcome-container {
          max-width: 860px;
          margin: 0 auto;
          padding: 60px 24px;
          font-family: 'Inter', -apple-system, sans-serif;
          animation: fadeIn 0.5s ease-out;
          background-color: #f0f0f0;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .welcome-header {
          text-align: center;
          margin-bottom: 56px;
        }

        .logo-badge {
          display: inline-flex;
          padding: 4px 12px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 11px;
          font-weight: 700;
          border-radius: 100px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 24px;
        }

        .welcome-header h1 {
          font-size: 32px;
          font-weight: 800;
          color: #0f172a;
          margin-bottom: 16px;
          letter-spacing: -0.02em;
        }

        .welcome-header p {
          font-size: 15px;
          line-height: 1.6;
          color: #64748b;
          max-width: 600px;
          margin: 0 auto;
        }

        .caps-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 56px;
        }

        .cap-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: #ffffff;
          border: 1px solid #f1f5f9;
          border-radius: 12px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .cap-card:hover {
          border-color: #2563eb;
          box-shadow: 0 4px 12px rgba(0,0,0,0.03);
        }

        .cap-icon {
          width: 40px;
          height: 40px;
          background: #f8fafc;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #2563eb;
          flex-shrink: 0;
        }

        .cap-content h3 {
          font-size: 14px;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 4px 0;
        }

        .cap-content p {
          font-size: 12.5px;
          line-height: 1.5;
          color: #64748b;
          margin: 0;
        }

        .guide-section {
          background: #f8fafc;
          border-radius: 16px;
          padding: 32px;
          border: 1px solid #f1f5f9;
        }

        .guide-header {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 24px;
        }

        .guide-steps {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 32px;
        }

        .g-step {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .g-num {
          width: 24px;
          height: 24px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 700;
          color: #1e293b;
        }

        .g-text strong {
          display: block;
          font-size: 13px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 4px;
        }

        .g-text p {
          font-size: 11.5px;
          line-height: 1.5;
          color: #64748b;
          margin: 0;
        }

        .text-blue { color: #2563eb; }

        @media (max-width: 640px) {
          .caps-grid, .guide-steps {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default WelcomeScreen;
